import { useState, useCallback } from 'react';
export function useForm({ initialValues, validationRules = {}, onSubmit }) {
    const [values, setValues] = useState(initialValues);
    const [errors, setErrors] = useState({});
    const [touched, setTouched] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const validateField = useCallback((name, value) => {
        const rules = validationRules[name];
        if (!rules)
            return '';
        // Required validation
        if (rules.required) {
            const isEmpty = value === '' || value === null || value === undefined;
            if (isEmpty) {
                return typeof rules.required === 'string'
                    ? rules.required
                    : `${name} é obrigatório`;
            }
        }
        // Skip other validations if value is empty and not required
        if (!value && !rules.required)
            return '';
        // MinLength validation
        if (rules.minLength) {
            const min = typeof rules.minLength === 'number'
                ? rules.minLength
                : rules.minLength.value;
            const message = typeof rules.minLength === 'object'
                ? rules.minLength.message
                : `Mínimo ${min} caracteres`;
            if (String(value).length < min) {
                return message;
            }
        }
        // MaxLength validation
        if (rules.maxLength) {
            const max = typeof rules.maxLength === 'number'
                ? rules.maxLength
                : rules.maxLength.value;
            const message = typeof rules.maxLength === 'object'
                ? rules.maxLength.message
                : `Máximo ${max} caracteres`;
            if (String(value).length > max) {
                return message;
            }
        }
        // Pattern validation
        if (rules.pattern && !rules.pattern.value.test(String(value))) {
            return rules.pattern.message;
        }
        // Custom validation
        if (rules.validate) {
            const result = rules.validate(value);
            if (typeof result === 'string') {
                return result;
            }
            if (result === false) {
                return 'Valor inválido';
            }
        }
        return '';
    }, [validationRules]);
    const validateForm = useCallback(() => {
        const newErrors = {};
        let isValid = true;
        Object.keys(validationRules).forEach(fieldName => {
            const error = validateField(fieldName, values[fieldName]);
            if (error) {
                newErrors[fieldName] = error;
                isValid = false;
            }
        });
        setErrors(newErrors);
        return isValid;
    }, [values, validationRules, validateField]);
    const setValue = useCallback((name, value) => {
        setValues(prev => ({ ...prev, [name]: value }));
        // Clear error when user starts typing
        if (errors[name]) {
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[name];
                return newErrors;
            });
        }
    }, [errors]);
    const setFieldValue = setValue;
    const setFieldError = useCallback((name, error) => {
        setErrors(prev => ({ ...prev, [name]: error }));
    }, []);
    const setFieldTouched = useCallback((name, isTouched = true) => {
        setTouched(prev => ({ ...prev, [name]: isTouched }));
        // Validate field when touched
        if (isTouched) {
            const error = validateField(name, values[name]);
            if (error) {
                setErrors(prev => ({ ...prev, [name]: error }));
            }
        }
    }, [validateField, values]);
    const handleChange = useCallback((e) => {
        const { name, value, type } = e.target;
        let processedValue = value;
        // Handle different input types
        if (type === 'checkbox') {
            processedValue = e.target.checked;
        }
        else if (type === 'number') {
            processedValue = value === '' ? '' : Number(value);
        }
        setValue(name, processedValue);
    }, [setValue]);
    const handleBlur = useCallback((e) => {
        const { name } = e.target;
        setFieldTouched(name);
    }, [setFieldTouched]);
    const handleSubmit = useCallback(async (e) => {
        if (e) {
            e.preventDefault();
        }
        // Mark all fields as touched
        const allFields = Object.keys(validationRules);
        setTouched(allFields.reduce((acc, field) => ({ ...acc, [field]: true }), {}));
        const isValid = validateForm();
        if (!isValid || !onSubmit) {
            return;
        }
        setIsSubmitting(true);
        try {
            await onSubmit(values);
        }
        catch (error) {
            console.error('Form submission error:', error);
        }
        finally {
            setIsSubmitting(false);
        }
    }, [validateForm, onSubmit, values, validationRules]);
    const reset = useCallback((newValues) => {
        setValues(newValues ? { ...initialValues, ...newValues } : initialValues);
        setErrors({});
        setTouched({});
        setIsSubmitting(false);
    }, [initialValues]);
    const setFormValues = useCallback((newValues) => {
        setValues(prev => ({ ...prev, ...newValues }));
    }, []);
    const getFieldProps = useCallback((name) => ({
        name: name,
        value: values[name] || '',
        onChange: handleChange,
        onBlur: handleBlur,
        error: touched[name] ? errors[name] : undefined
    }), [values, errors, touched, handleChange, handleBlur]);
    const isValid = Object.keys(errors).length === 0;
    return {
        values,
        errors,
        touched,
        isSubmitting,
        isValid,
        setValue: setFieldValue,
        setFieldValue,
        setFieldError,
        setFieldTouched,
        setFormValues,
        handleChange,
        handleBlur,
        handleSubmit,
        validateForm,
        validateField,
        reset,
        getFieldProps
    };
}
